import React, { useEffect, useState, useCallback } from 'react';
import { useProdAuth } from '../../contexts/ProdAuthContext';

const ACCENT = '#4A7C59';

interface ManagerUser {
  id: string;
  name: string;
  email: string;
}

interface Centre {
  id: string;
  name: string;
  centreType: string | null;
  county: string | null;
  ward: string | null;
  gpsLat: string | null;
  gpsLng: string | null;
  managerName: string | null;
  managerPhone: string | null;
  managerIdNumber: string | null;
  active: boolean;
  createdAt: string;
  farmerCount: number;
  todayDeliveryCount: number;
  todayKg: string;
  activeBatches: number;
  managerUser: ManagerUser | null;
}

const emptyForm = {
  name: '',
  centreType: 'station' as 'station' | 'hub',
  county: '',
  ward: '',
  gpsLat: '',
  gpsLng: '',
  managerName: '',
  managerPhone: '',
  managerIdNumber: '',
};

type FormState = typeof emptyForm;

function StatPill({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">{label}</span>
      <span className="text-base font-bold text-[#1A1A1A]">{value}</span>
    </div>
  );
}

function GPSButton({ onCapture }: { onCapture: (lat: string, lng: string) => void }) {
  const [capturing, setCapturing] = useState(false);
  const [err, setErr] = useState('');

  const capture = () => {
    if (!navigator.geolocation) { setErr('Geolocation not supported'); return; }
    setCapturing(true);
    setErr('');
    navigator.geolocation.getCurrentPosition(
      pos => {
        onCapture(String(pos.coords.latitude), String(pos.coords.longitude));
        setCapturing(false);
      },
      () => { setErr('Could not get location'); setCapturing(false); },
      { timeout: 10000 },
    );
  };

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={capture}
        disabled={capturing}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-[#E8E6E2] text-[#4A7C59] hover:bg-[#F0EDE8] transition-colors disabled:opacity-50"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        {capturing ? 'Locating...' : 'Use my location'}
      </button>
      {err && <span className="text-xs text-red-500">{err}</span>}
    </div>
  );
}

function CentreFormModal({
  title,
  form,
  setForm,
  onClose,
  onSubmit,
  saving,
  error,
}: {
  title: string;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  saving: boolean;
  error: string;
}) {
  const field = (key: keyof FormState, label: string, placeholder = '', required = false) => (
    <div key={key}>
      <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type="text"
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        required={required}
        className="w-full px-4 py-2.5 border border-[#E8E6E2] rounded-lg text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#4A7C59]/30"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-4">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-[#E8E6E2]">
          <h3 className="text-lg font-bold text-[#1A1A1A]">{title}</h3>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-[#1A1A1A] transition-colors">✕</button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
        )}

        <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
          {/* Centre type toggle */}
          <div>
            <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-2">Centre type</label>
            <div className="flex gap-2">
              {(['station', 'hub'] as const).map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, centreType: t }))}
                  className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors capitalize ${
                    form.centreType === t
                      ? 'border-[#4A7C59] bg-[#4A7C59] text-white'
                      : 'border-[#E8E6E2] text-[#6B6B6B] hover:bg-[#F0EDE8]'
                  }`}
                >
                  {t === 'hub' ? 'Hub' : 'Station'}
                </button>
              ))}
            </div>
          </div>

          {field('name', 'Centre name', 'e.g. Misadhi Station', true)}

          <div className="grid grid-cols-2 gap-4">
            {field('county', 'County', 'e.g. Kisii')}
            {field('ward', 'Ward', 'e.g. Monyerero')}
          </div>

          {/* GPS */}
          <div>
            <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-2">GPS Location</label>
            <GPSButton onCapture={(lat, lng) => setForm(f => ({ ...f, gpsLat: lat, gpsLng: lng }))} />
            <div className="grid grid-cols-2 gap-3 mt-2">
              <input
                type="text"
                value={form.gpsLat}
                onChange={e => setForm(f => ({ ...f, gpsLat: e.target.value }))}
                placeholder="Latitude"
                className="w-full px-3 py-2 border border-[#E8E6E2] rounded-lg text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#4A7C59]/30"
              />
              <input
                type="text"
                value={form.gpsLng}
                onChange={e => setForm(f => ({ ...f, gpsLng: e.target.value }))}
                placeholder="Longitude"
                className="w-full px-3 py-2 border border-[#E8E6E2] rounded-lg text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#4A7C59]/30"
              />
            </div>
          </div>

          {/* Manager KYC */}
          <div className="border-t border-[#E8E6E2] pt-4">
            <p className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider mb-3">Station manager KYC</p>
            <div className="space-y-3">
              {field('managerName', 'Full name', 'e.g. James Otieno')}
              {field('managerPhone', 'Phone number', 'e.g. 0712345678')}
              {field('managerIdNumber', 'National ID number', 'e.g. 12345678')}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 text-sm text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: ACCENT }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export const CentreList: React.FC = () => {
  const { user } = useProdAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';

  const [centres, setCentres] = useState<Centre[]>([]);
  const [loading, setLoading] = useState(true);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<FormState>({ ...emptyForm });
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState('');

  // Edit modal
  const [editCentre, setEditCentre] = useState<Centre | null>(null);
  const [editForm, setEditForm] = useState<FormState>({ ...emptyForm });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete confirm
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCentres = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/production/collection-centres', { credentials: 'include' });
      const data = await res.json();
      setCentres(data.centres || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCentres(); }, [fetchCentres]);

  // ── Create ────────────────────────────────────────────────────────────────────
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateSaving(true);
    setCreateError('');
    try {
      const res = await fetch('/api/production/collection-centres', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(createForm),
      });
      if (!res.ok) {
        const d = await res.json();
        setCreateError(d.error || 'Failed to create centre');
        return;
      }
      setShowCreate(false);
      setCreateForm({ ...emptyForm });
      fetchCentres();
    } finally {
      setCreateSaving(false);
    }
  };

  // ── Edit ──────────────────────────────────────────────────────────────────────
  const openEdit = (c: Centre) => {
    setEditCentre(c);
    setEditForm({
      name: c.name,
      centreType: (c.centreType as 'station' | 'hub') || 'station',
      county: c.county || '',
      ward: c.ward || '',
      gpsLat: c.gpsLat || '',
      gpsLng: c.gpsLng || '',
      managerName: c.managerName || '',
      managerPhone: c.managerPhone || '',
      managerIdNumber: c.managerIdNumber || '',
    });
    setEditError('');
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editCentre) return;
    setEditSaving(true);
    setEditError('');
    try {
      const res = await fetch(`/api/production/collection-centres?id=${editCentre.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editForm),
      });
      if (!res.ok) {
        const d = await res.json();
        setEditError(d.error || 'Failed to update centre');
        return;
      }
      setEditCentre(null);
      fetchCentres();
    } finally {
      setEditSaving(false);
    }
  };

  // ── Toggle active ─────────────────────────────────────────────────────────────
  const toggleActive = async (c: Centre) => {
    await fetch(`/api/production/collection-centres?id=${c.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ...c, active: !c.active }),
    });
    fetchCentres();
  };

  // ── Delete ────────────────────────────────────────────────────────────────────
  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      await fetch(`/api/production/collection-centres?id=${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setDeleteId(null);
      fetchCentres();
    } finally {
      setDeleting(false);
    }
  };

  const hubs = centres.filter(c => c.centreType === 'hub');
  const stations = centres.filter(c => c.centreType !== 'hub');

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1A1A1A]">Collection centres</h2>
          <p className="text-sm text-[#6B6B6B] mt-0.5">
            {hubs.length} hub{hubs.length !== 1 ? 's' : ''} · {stations.length} station{stations.length !== 1 ? 's' : ''}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => { setCreateForm({ ...emptyForm }); setCreateError(''); setShowCreate(true); }}
            className="px-4 py-2.5 text-sm font-medium text-white rounded-lg flex items-center gap-2"
            style={{ backgroundColor: ACCENT }}
          >
            <span>+</span> Add centre
          </button>
        )}
      </div>

      {/* ── Hubs ─────────────────────────────────────────────────────────────── */}
      {hubs.length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-semibold text-[#A3A3A3] uppercase tracking-widest mb-3">Hubs</p>
          <div className="grid gap-4">
            {hubs.map(c => (
              <HubCard
                key={c.id}
                centre={c}
                isAdmin={isAdmin}
                isSuperAdmin={isSuperAdmin}
                onEdit={() => openEdit(c)}
                onToggle={() => toggleActive(c)}
                onDelete={() => setDeleteId(c.id)}
                deleteId={deleteId}
                deleting={deleting}
                onConfirmDelete={handleDelete}
                onCancelDelete={() => setDeleteId(null)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Stations ─────────────────────────────────────────────────────────── */}
      <p className="text-xs font-semibold text-[#A3A3A3] uppercase tracking-widest mb-3">Stations</p>
      {loading ? (
        <div className="py-16 text-center text-sm text-[#6B6B6B]">Loading...</div>
      ) : stations.length === 0 ? (
        <div className="py-16 text-center text-sm text-[#6B6B6B]">No stations yet</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {stations.map(c => (
            <StationCard
              key={c.id}
              centre={c}
              isAdmin={isAdmin}
              isSuperAdmin={isSuperAdmin}
              onEdit={() => openEdit(c)}
              onToggle={() => toggleActive(c)}
              onDelete={() => setDeleteId(c.id)}
              deleteId={deleteId}
              deleting={deleting}
              onConfirmDelete={handleDelete}
              onCancelDelete={() => setDeleteId(null)}
            />
          ))}
        </div>
      )}

      {/* ── Create modal ──────────────────────────────────────────────────────── */}
      {showCreate && (
        <CentreFormModal
          title="New collection centre"
          form={createForm}
          setForm={setCreateForm}
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
          saving={createSaving}
          error={createError}
        />
      )}

      {/* ── Edit modal ────────────────────────────────────────────────────────── */}
      {editCentre && (
        <CentreFormModal
          title={`Edit — ${editCentre.name}`}
          form={editForm}
          setForm={setEditForm}
          onClose={() => setEditCentre(null)}
          onSubmit={handleEdit}
          saving={editSaving}
          error={editError}
        />
      )}
    </div>
  );
};

// ── Hub card ──────────────────────────────────────────────────────────────────
interface CardProps {
  centre: Centre;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
  deleteId: string | null;
  deleting: boolean;
  onConfirmDelete: (id: string) => void;
  onCancelDelete: () => void;
}

function HubCard({ centre: c, isAdmin, isSuperAdmin, onEdit, onToggle, onDelete, deleteId, deleting, onConfirmDelete, onCancelDelete }: CardProps) {
  return (
    <div className="bg-gradient-to-br from-[#4A7C59] to-[#3d6649] text-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold bg-white/20 px-2.5 py-0.5 rounded-full uppercase tracking-wide">Hub</span>
            {!c.active && <span className="text-xs bg-black/20 px-2 py-0.5 rounded-full">Inactive</span>}
          </div>
          <h3 className="text-xl font-bold mt-2">{c.name}</h3>
          {(c.county || c.ward) && (
            <p className="text-sm text-white/70 mt-0.5">{[c.county, c.ward].filter(Boolean).join(', ')}</p>
          )}
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button onClick={onEdit} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-4 gap-4 bg-white/10 rounded-xl px-4 py-3">
        <div className="flex flex-col">
          <span className="text-[10px] text-white/60 uppercase tracking-wider">Farmers</span>
          <span className="text-lg font-bold">{c.farmerCount}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] text-white/60 uppercase tracking-wider">Deliveries today</span>
          <span className="text-lg font-bold">{c.todayDeliveryCount}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] text-white/60 uppercase tracking-wider">Cherry today</span>
          <span className="text-lg font-bold">{parseFloat(c.todayKg || '0').toLocaleString()} kg</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] text-white/60 uppercase tracking-wider">Active batches</span>
          <span className="text-lg font-bold">{c.activeBatches}</span>
        </div>
      </div>

      {c.managerName && (
        <div className="mt-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">
            {c.managerName.charAt(0)}
          </div>
          <div>
            <p className="text-sm font-medium">{c.managerName}</p>
            <div className="flex gap-3 text-xs text-white/60">
              {c.managerPhone && <span>{c.managerPhone}</span>}
              {c.managerIdNumber && <span>ID: {c.managerIdNumber}</span>}
            </div>
          </div>
        </div>
      )}

      {c.gpsLat && c.gpsLng && (
        <a
          href={`https://maps.google.com/?q=${c.gpsLat},${c.gpsLng}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-4 text-xs text-white/70 hover:text-white transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          </svg>
          View on map
        </a>
      )}
    </div>
  );
}

// ── Station card ──────────────────────────────────────────────────────────────
function StationCard({ centre: c, isAdmin, isSuperAdmin, onEdit, onToggle, onDelete, deleteId, deleting, onConfirmDelete, onCancelDelete }: CardProps) {
  return (
    <div className={`bg-white border rounded-xl p-5 flex flex-col gap-4 ${c.active ? 'border-[#E8E6E2]' : 'border-[#E8E6E2] opacity-60'}`}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold bg-[#F0EDE8] text-[#6B6B6B] px-2 py-0.5 rounded-full uppercase tracking-wide">Station</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.active ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'}`}>
              {c.active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <h3 className="font-semibold text-[#1A1A1A] text-base">{c.name}</h3>
          {(c.county || c.ward) && (
            <p className="text-xs text-[#A3A3A3] mt-0.5">{[c.county, c.ward].filter(Boolean).join(', ')}</p>
          )}
        </div>
        {c.gpsLat && c.gpsLng && (
          <a
            href={`https://maps.google.com/?q=${c.gpsLat},${c.gpsLng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-lg border border-[#E8E6E2] text-[#6B6B6B] hover:bg-[#F0EDE8] transition-colors"
            title="View on map"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            </svg>
          </a>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-3 bg-[#F9F8F6] rounded-xl px-3 py-2.5">
        <StatPill label="Farmers" value={c.farmerCount} />
        <StatPill label="Deliveries" value={c.todayDeliveryCount} />
        <StatPill label="Cherry today" value={`${parseFloat(c.todayKg || '0').toLocaleString()} kg`} />
        <StatPill label="Batches" value={c.activeBatches} />
      </div>

      {/* Manager */}
      {c.managerName ? (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#4A7C59]/10 flex items-center justify-center text-[#4A7C59] text-sm font-bold flex-shrink-0">
            {c.managerName.charAt(0)}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#1A1A1A] truncate">{c.managerName}</p>
            <div className="flex gap-3 text-xs text-[#A3A3A3]">
              {c.managerPhone && <span>{c.managerPhone}</span>}
              {c.managerIdNumber && <span>ID: {c.managerIdNumber}</span>}
            </div>
          </div>
          {c.managerUser && (
            <span className="ml-auto text-xs text-[#4A7C59] bg-[#4A7C59]/10 px-2 py-0.5 rounded-full flex-shrink-0">Account linked</span>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-xs text-[#A3A3A3]">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          No manager assigned
        </div>
      )}

      {/* Actions */}
      {isAdmin && (
        <div className="flex items-center gap-2 pt-1 border-t border-[#E8E6E2]">
          <button
            onClick={onEdit}
            className="text-xs text-[#6B6B6B] px-3 py-1.5 rounded-lg border border-[#E8E6E2] hover:bg-[#F0EDE8] transition-colors"
          >
            Edit
          </button>
          <button
            onClick={onToggle}
            className="text-xs text-[#6B6B6B] px-3 py-1.5 rounded-lg border border-[#E8E6E2] hover:bg-[#F0EDE8] transition-colors"
          >
            {c.active ? 'Deactivate' : 'Activate'}
          </button>
          {isSuperAdmin && deleteId === c.id ? (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-[#6B6B6B]">Delete?</span>
              <button
                onClick={() => onConfirmDelete(c.id)}
                disabled={deleting}
                className="text-xs text-white bg-red-500 px-3 py-1.5 rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
              >
                {deleting ? '...' : 'Yes'}
              </button>
              <button
                onClick={onCancelDelete}
                className="text-xs text-[#6B6B6B] px-3 py-1.5 rounded-lg border border-[#E8E6E2] hover:bg-[#F0EDE8] transition-colors"
              >
                No
              </button>
            </div>
          ) : isSuperAdmin ? (
            <button
              onClick={onDelete}
              className="ml-auto text-xs text-red-400 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
            >
              Delete
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
